import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Lock } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { VoucherCard, ukToday, addDays, longDate } from "@/components/VoucherCard";
import { field, Notice } from "@/pages/Auth";
import { GiftOptions, giftItems, giftTotal, includesText } from "@/components/GiftOptions";
import { api, apiError } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function checkGift(f) {
  if (!f.buyer_name.trim()) return "Please enter your name.";
  if (!EMAIL_RE.test(f.buyer_email.trim())) return "Please enter a valid email address for yourself.";
  if (!f.recipient_name.trim()) return "Please enter the name of the person receiving the gift.";
  if (f.send && !EMAIL_RE.test(f.recipient_email.trim())) return "Please enter a valid email address for the person receiving the gift.";
  if (f.send && !f.send_on) return "Please choose a date to send the gift.";
  return "";
}

export function Field({ id, label, hint, children }) {
  return (
    <div>
      <label htmlFor={id} className="eyebrow block mb-2">{label}</label>
      {children}
      {hint && <p className="mt-1.5 text-sm text-[#8C84A0]">{hint}</p>}
    </div>
  );
}

export function DeliveryFields({ form, set, idPrefix = "gift" }) {
  const today = ukToday();
  return (
    <div className="space-y-5">
      <Field id={`${idPrefix}-recipient-email`} label="Their email address">
        <input id={`${idPrefix}-recipient-email`} type="email" className={field} value={form.recipient_email} onChange={set("recipient_email")} placeholder="their@email.com" data-testid={`${idPrefix}-recipient-email`} />
      </Field>
      <Field id={`${idPrefix}-send-on`} label="Send it on" hint="We'll email it at about 7am UK time on this date. Choose today to send it straight away.">
        <input id={`${idPrefix}-send-on`} type="date" min={today} max={addDays(today, 364)} className={`${field} [color-scheme:dark]`} value={form.send_on} onChange={set("send_on")} data-testid={`${idPrefix}-send-on`} />
        {form.send_on && <p className="mt-2 text-[#C9B6FF]" data-testid={`${idPrefix}-send-on-readable`}>{longDate(form.send_on)}</p>}
      </Field>
    </div>
  );
}

export default function GiftPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", recipient_name: "", message: "", send: false, recipient_email: "", send_on: ukToday() });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(params.get("plan") === "twelve" ? "twelve" : "six");
  const [story, setStory] = useState(false);
  const [storyAvailable, setStoryAvailable] = useState(false);
  useEffect(() => { api.get("/pricing").then((r) => setStoryAvailable(r.data.available.includes("story"))).catch(() => {}); }, []);
  const items = giftItems(plan, story && storyAvailable);
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    const problem = checkGift(form);
    if (problem) return setError(problem);
    setBusy(true);
    try {
      const { send, ...rest } = form;
      const { data } = await api.post("/gifts/checkout", { ...rest, items, origin_url: window.location.origin, recipient_email: send ? rest.recipient_email.trim() : null, send_on: send ? rest.send_on : null });
      window.location.assign(data.checkout_url);
    } catch (err) { setError(apiError(err)); setBusy(false); }
  };

  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
        <form onSubmit={submit} noValidate className="glass-card p-7 sm:p-9 space-y-5 fade-up" data-testid="gift-form">
          <p className="eyebrow">Give Memories Forever</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#EDE6F7]" data-testid="gift-heading">A gift of the voices they love</h1>
          <p className="text-lg text-[#B9B3C8]">Give someone the Memories Forever keepsake. They'll get a code to unlock it whenever they're ready. You don't need an account.</p>
          {params.get("cancelled") && <Notice tone="note" testid="gift-cancelled">Payment cancelled. You haven't been charged.</Notice>}
          <GiftOptions plan={plan} setPlan={setPlan} story={story} setStory={setStory} storyAvailable={storyAvailable} />
          <Field id="gift-buyer-name" label="Your name"><input id="gift-buyer-name" className={field} value={form.buyer_name} onChange={set("buyer_name")} maxLength={80} autoComplete="name" placeholder="e.g. Ross" data-testid="gift-buyer-name" /></Field>
          <Field id="gift-buyer-email" label="Your email address" hint="We'll send your receipt and printable voucher here."><input id="gift-buyer-email" type="email" className={field} value={form.buyer_email} onChange={set("buyer_email")} autoComplete="email" placeholder="you@example.com" data-testid="gift-buyer-email" /></Field>
          <Field id="gift-recipient-name" label="Who is it for?"><input id="gift-recipient-name" className={field} value={form.recipient_name} onChange={set("recipient_name")} maxLength={60} placeholder="e.g. Grandma Jean" data-testid="gift-recipient-name" /></Field>
          <Field id="gift-message" label="Your personal message (optional)" hint={`${form.message.length}/300`}>
            <textarea id="gift-message" rows={4} maxLength={300} className={`${field} h-auto py-3 resize-none`} value={form.message} onChange={set("message")} placeholder="A few words to go with your gift" data-testid="gift-message" />
          </Field>
          <label className="flex gap-3 items-start rounded-xl border border-[rgba(139,44,255,0.35)] bg-[#1A1222] p-4 cursor-pointer" data-testid="gift-send-option">
            <input type="checkbox" checked={form.send} onChange={set("send")} className="mt-1 h-5 w-5 accent-[#8B2CFF]" data-testid="gift-send-checkbox" />
            <span><span className="block text-[#EDE6F7] font-semibold">Email the voucher to them for me</span><span className="block text-sm text-[#B9B3C8]">Choose a date, such as Christmas morning. You'll always get a printable copy too.</span></span>
          </label>
          {form.send && <DeliveryFields form={form} set={set} />}
          {error && <Notice tone="error" testid="gift-error">{error}</Notice>}
          <button type="submit" disabled={busy} className="gold-btn w-full min-h-16 py-3 rounded-full text-lg sm:text-xl disabled:opacity-60" data-testid="gift-submit">
            {busy ? "Opening secure checkout…" : `Continue to secure payment · ${giftTotal(items)}`}
          </button>
          <p className="flex gap-2 text-sm text-[#A49CB8]"><Lock className="h-4 w-4 mt-0.5 shrink-0 text-[#B57BFF]" aria-hidden="true" />One-off payment by Stripe. No subscription. The code is valid for 12 months.</p>
        </form>
        <div className="lg:sticky lg:top-24 space-y-4" data-testid="gift-preview">
          <p className="eyebrow">Preview of your voucher</p>
          <VoucherCard recipient={form.recipient_name.trim()} buyer={form.buyer_name.trim()} message={form.message.trim()} includes={includesText(items)} testid="gift-preview-card" />
          <p className="text-sm text-[#8C84A0]">Your code appears once payment is complete.</p>
        </div>
      </main>
    </div>
  );
}
